import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { ProgramCard, ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { faqSchema } from "@/lib/schema";

const FAQS = [
  { q: "What is Generative Engine Optimization (GEO)?", a: "GEO optimizes content, structure and entity signals so your brand appears inside Google AI Overviews, Google AI Mode, and other generative search experiences that synthesize answers from multiple sources." },
  { q: "What do GEO services include?", a: "An audit of which of your target searches show AI Overviews, passage-level rewrites so your pages are the quoted source, schema and entity fixes, original data worth citing, and monthly tracking of AI Overview and AI Mode visibility." },
  { q: "Are these geospatial (GIS) services?", a: "No. Here GEO means Generative Engine Optimization — getting your business cited in AI-generated search answers. It has nothing to do with maps, GIS or geolocation data." },
  { q: "Is GEO the same as AEO?", a: "They overlap but aren't identical. AEO targets standalone AI assistants (ChatGPT, Perplexity, Claude). GEO targets generative answers inside search engines themselves — primarily Google AI Overviews and AI Mode." },
  { q: "Does GEO replace SEO?", a: "No. GEO sits on top of strong classic SEO. AI Overviews are generated from pages Google already trusts, so your blue-link rankings, entity authority and schema all feed GEO performance." },
  { q: "How do I track GEO performance?", a: "Through AI Overview impression data in Search Console, manual tracking of target queries in Google AI Mode, share-of-voice tools (e.g. Profound, Otterly), and branded mention monitoring." },
];

const DELIVERABLES = [
  "AI Overview eligibility audit (which queries you can win)",
  "Passage-level optimization for generative answers",
  "Entity, FAQ, HowTo & Article schema build-out",
  "Citation-magnet content (data, statistics, original research)",
  "Google AI Mode + AI Overview tracking dashboard",
  "Quarterly generative share-of-voice report",
];

const OUTCOMES = ["Featured in AI Overviews", "Visibility in Google AI Mode", "Defended traffic post-AI-search rollout"];

export const Route = createFileRoute("/services/geo")({
  head: () => buildSeo({
    title: "GEO Services — Generative Engine Optimization for AI Overviews | Freelancer Tamal",
    description: "GEO (Generative Engine Optimization) services to get your business cited in Google AI Overviews and AI Mode: eligibility audit, passage rewrites, schema, and monthly tracking. See prices.",
    path: "/services/geo",
    alternates: { bn: "/bn/services/geo" },
    ogEyebrow: "GEO",
    keywords: "GEO services, GEO expert Bangladesh, generative engine optimization, Google AI Overviews, Google AI Mode, AI search optimization, SGE optimization",
  }),
  component: GeoPage,
});

function GeoPage() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "GEO", path: "/services/geo" }]} />
      <Section headingLevel={1}
        eyebrow="Generative Engine Optimization"
        title={<>Win <span className="text-gradient">Google AI Overviews</span> & AI Mode.</>}
        subtitle="GEO turns your highest-intent pages into the passages Google's generative answers quote — defending traffic in the AI-search era."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "GEO earns your brand visibility inside Google AI Overviews, AI Mode, and other generative SERP features.",
              "It compounds your existing SEO: AI Overviews are sourced from pages Google already ranks and trusts.",
              "Without GEO, you watch competitors get cited on the same SERPs you used to dominate.",
            ]}
          />
          <p className="mt-6 text-muted-foreground">
            <strong className="text-foreground">GEO services</strong> here means Generative Engine Optimization (not geospatial/GIS work). See the GEO Retainer on the{" "}
            <Link to="/pricing" className="text-primary underline underline-offset-4">pricing page</Link>, or add{" "}
            <Link to="/services/aeo" className="text-primary underline underline-offset-4">AEO services</Link> for ChatGPT and Perplexity.
          </p>
          <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />
        </div>
      </Section>

      <ProgramCard>
        <ProgramIntro label="Service" title="GEO program" tagline="A focused engagement to identify, optimize and track Google AI Overview and AI Mode opportunities for your highest-revenue queries.">
          <div className="mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
              Start a GEO engagement <ArrowUpRight size={16} />
            </Link>
          </div>
        </ProgramIntro>
        <ProgramDetails deliverables={DELIVERABLES} outcomes={OUTCOMES}>
          <ProgramRow heading="People also ask">
            <FaqAccordion items={FAQS} idPrefix="faq-geo" withSchema={false} />
          </ProgramRow>
        </ProgramDetails>
      </ProgramCard>

      <Section title={<>Pair GEO with <span className="text-gradient">AEO</span> for full AI-search coverage.</>}>
        <div className="flex flex-wrap gap-3">
          <Link to="/services/aeo" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">See the AEO program <ArrowUpRight size={16} /></Link>
          <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">All services <ArrowUpRight size={16} /></Link>
        </div>
        <AiAnswersCallout
          context="Google AI Overviews & generative search"
          questionSlugs={[
            "geo-what-is",
            "what-is-google-ai-overviews",
            "chatgpt-vs-perplexity-vs-gemini",
            "what-is-entity-seo",
          ]}
        />
      </Section>

      <RelatedReading
        eyebrow="Deep-dive guides"
        title={<>How Google AI Overviews <span className="text-gradient">actually pick sources.</span></>}
        hrefs={[
          "/blog/google-ai-overviews-citation-report-2026",
          "/blog/1000-ai-overview-results-citation-pattern",
          "/blog/ai-overviews-vs-featured-snippets-2026",
          "/blog/ai-overviews-ymyl-citation-audit",
          "/blog/schema-markup-for-aeo-2026",
          "/blog/measure-aeo-performance-2026",
        ]}
      />

      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Service",
        name: "Generative Engine Optimization (GEO)",
        serviceType: "Generative Engine Optimization",
        description: "Win Google AI Overviews, AI Mode and generative search visibility with passage optimization, schema, and citation-magnet content.",
        areaServed: ["Bangladesh", "Worldwide"],
        provider: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/about`, jobTitle: "SEO & GEO Expert", sameAs: Object.values(SITE.social) },
      }} />
      <JsonLd data={faqSchema(FAQS)} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "GEO", path: "/services/geo" }])} />
    </>
  );
}